import { EventEmitter, Injectable, Output } from '@angular/core';
import { ExcelService } from './ExcelService';
import { CalibartionValue, ExportCalibartionValue } from '../system/admin-panel/Objects/Models/TrendViewModel';

@Injectable({
  providedIn: 'root',
})
export class CalibrationsImportService {
  @Output() onImported: EventEmitter<CalibartionValue[]> = new EventEmitter();

  constructor(private excelService: ExcelService) {
    this.excelService.onImported.subscribe(data => {
      const calibrationTable: CalibartionValue[] = [];

      try {
        for (const value of data) {
          const val = new CalibartionValue();
          val.RawValue = this.toNumber(value['SensorValue']);
          val.Value = this.toNumber(value['TrendValue']);
          val.Title = value['Title'];
          if (Number.isFinite(val.RawValue) && Number.isFinite(val.Value)) {
            calibrationTable.push(val);
          }
        }
      } catch (ex) {
        console.error('Excel import mapping failed', ex);
      }

      this.onImported.emit(calibrationTable);
    });
  }

  public Export(calibrations: ExportCalibartionValue[]) {
    this.excelService.exportJSONAsExcelFile(calibrations);
  }

  public Import(file: File) {
    const fileName = (file?.name ?? '').toLowerCase();

    if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      return this.excelService.Upload(file);
    }

    if (fileName.endsWith('.txt') || fileName.endsWith('.csv')) {
      const fileReader = new FileReader();

      fileReader.onload = () => {
        const text = (fileReader.result as string) ?? '';
        const parsed = this.parseCalibrationText(text);
        this.onImported.emit(parsed);
      };

      fileReader.onerror = () => {
        console.error('Failed to read text file', fileReader.error);
        this.onImported.emit([]);
      };

      fileReader.readAsText(file, 'utf-8');
      return;
    }

    this.onImported.emit([]);
  }

  private parseCalibrationText(text: string): CalibartionValue[] {
    const lines = this.normalizeLines(text);
    if (lines.length === 0) return [];

    const firstParsed = this.tryParseDataLine(lines[0]);
    const startIndex = firstParsed ? 0 : 1;

    const result: CalibartionValue[] = [];

    for (let i = startIndex; i < lines.length; i++) {
      const parsed = this.tryParseDataLine(lines[i]);
      if (!parsed) continue;

      const item = new CalibartionValue();
      item.RawValue = parsed.raw;
      item.Value = parsed.val;
      item.Title = parsed.title ?? '';

      result.push(item);
    }

    return result;
  }

  private tryParseDataLine(line: string): { raw: number; val: number; title?: string } | null {
    if (!line) return null;

    const cleaned = this.stripOuterQuotes(line.trim());

    const tokens = cleaned
      .split(/[\s,;:\-]+/g)
      .map(t => this.stripOuterQuotes(t.trim()))
      .filter(Boolean);

    if (tokens.length < 2) return null;

    const raw = this.toNumber(tokens[0]);
    const val = this.toNumber(tokens[1]);

    if (!Number.isFinite(raw) || !Number.isFinite(val)) return null;

    const title = tokens.length >= 3 ? tokens.slice(2).join(' ') : undefined;

    return { raw, val, title };
  }

  private normalizeLines(text: string): string[] {
    return (text ?? '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .map(l => l.trim())
      .filter(l => l.length > 0)
      .filter(l => !l.startsWith('#') && !l.startsWith('//'));
  }

  private stripOuterQuotes(value: string): string {
    if (!value) return value;
    const v = value.trim();
    if (v.length >= 2 && v.startsWith('"') && v.endsWith('"')) {
      return v.substring(1, v.length - 1).trim();
    }
    return v;
  }

  private toNumber(value: any): number {
    if (value == null) return Number.NaN;
    const v = String(value).trim().replace(',', '.');
    return Number(v);
  }
}
